import { useEffect, useState } from "react";
import { api, formatError } from "../lib/api";
import { PageHeader, Modal } from "../components/ui-kit";
import { Plus, Trash2, Pencil, ChevronRight, ChevronDown, Send, Undo2 } from "lucide-react";
import { useAuth } from "../lib/auth";

const STATUS_LABEL = {
  estoque: { label: "Em Estoque", cls: "badge-open" },
  com_tecnico: { label: "Com Técnico", cls: "badge-progress" },
  instalado: { label: "Instalado", cls: "badge-done" },
  defeito: { label: "Defeito", cls: "badge-cancel" },
  baixado: { label: "Baixado", cls: "badge-role-tecnico" },
};

export default function EquipmentPage() {
  const { user } = useAuth();
  const canManage = ["admin", "gestor"].includes(user?.role);
  const [models, setModels] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [unitsByModel, setUnitsByModel] = useState({});
  const [techs, setTechs] = useState([]);

  const [modelOpen, setModelOpen] = useState(false);
  const [modelForm, setModelForm] = useState({ name: "", brand: "", category: "", description: "" });
  const [editingModel, setEditingModel] = useState(null);

  const [snOpen, setSnOpen] = useState(false);
  const [snForm, setSnForm] = useState({ model_id: "", serial_numbers_text: "" });
  const [snMsg, setSnMsg] = useState("");

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({ unit: null, technician_id: "" });

  const [err, setErr] = useState("");

  const loadModels = () => api.get("/equipment-models").then((r) => setModels(r.data));
  const loadUnits = (mid) => api.get(`/equipment-models/${mid}/units`).then((r) =>
    setUnitsByModel((prev) => ({ ...prev, [mid]: r.data }))
  );

  useEffect(() => {
    loadModels();
    if (canManage) api.get("/users").then((r) => setTechs(r.data.filter((u) => u.role === "tecnico")));
  }, [canManage]);

  const toggle = (mid) => {
    const next = !expanded[mid];
    setExpanded({ ...expanded, [mid]: next });
    if (next && !unitsByModel[mid]) loadUnits(mid);
  };

  const openNewModel = () => { setEditingModel(null); setModelForm({ name: "", brand: "", category: "", description: "" }); setErr(""); setModelOpen(true); };
  const openEditModel = (m) => { setEditingModel(m.id); setModelForm({ name: m.name, brand: m.brand || "", category: m.category || "", description: m.description || "" }); setErr(""); setModelOpen(true); };

  const saveModel = async () => {
    setErr("");
    try {
      if (editingModel) await api.put(`/equipment-models/${editingModel}`, modelForm);
      else await api.post("/equipment-models", modelForm);
      setModelOpen(false); loadModels();
    } catch (e) { setErr(formatError(e)); }
  };

  const deleteModel = async (id) => {
    if (!window.confirm("Excluir este modelo?")) return;
    try { await api.delete(`/equipment-models/${id}`); loadModels(); }
    catch (e) { alert(formatError(e)); }
  };

  const openAddSns = (mid) => { setSnForm({ model_id: mid, serial_numbers_text: "" }); setSnMsg(""); setSnOpen(true); };

  const submitSns = async () => {
    setSnMsg("");
    const lines = snForm.serial_numbers_text.split(/[\n,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (lines.length === 0) { setSnMsg("Informe ao menos um número de série"); return; }
    try {
      const { data } = await api.post(`/equipment-models/${snForm.model_id}/units`, { serial_numbers: lines });
      let msg = `${data.created.length} unidade(s) cadastrada(s)`;
      if (data.duplicates.length) msg += ` · ${data.duplicates.length} duplicada(s) ignorada(s)`;
      setSnMsg(msg);
      loadUnits(snForm.model_id);
      loadModels();
      setSnForm({ ...snForm, serial_numbers_text: "" });
    } catch (e) { setSnMsg(formatError(e)); }
  };

  const openAssign = (unit) => { setAssignForm({ unit, technician_id: "" }); setAssignOpen(true); };

  const doAssign = async () => {
    try {
      await api.post(`/equipment-units/${assignForm.unit.id}/assign`, { technician_id: assignForm.technician_id });
      setAssignOpen(false);
      loadUnits(assignForm.unit.model_id);
      loadModels();
    } catch (e) { alert(formatError(e)); }
  };

  const returnUnit = async (u) => {
    if (!window.confirm(`Retornar unidade SN ${u.serial_number} ao estoque?`)) return;
    try {
      await api.post(`/equipment-units/${u.id}/return`);
      loadUnits(u.model_id);
      loadModels();
    } catch (e) { alert(formatError(e)); }
  };

  const deleteUnit = async (u) => {
    if (!window.confirm(`Excluir unidade SN ${u.serial_number}?`)) return;
    try {
      await api.delete(`/equipment-units/${u.id}`);
      loadUnits(u.model_id);
      loadModels();
    } catch (e) { alert(formatError(e)); }
  };

  return (
    <div data-testid="equipment-page">
      <PageHeader
        eyebrow="Inventário Serializado"
        title="Equipamentos"
        actions={canManage && (
          <button className="btn btn-primary" onClick={openNewModel} data-testid="new-equipment-model-btn">
            <Plus size={16} /> Novo Modelo
          </button>
        )}
      >
        Cada modelo (ex.: Router ZTE WiFi 6) reúne várias unidades físicas, cada uma com número de série próprio.
      </PageHeader>

      {models.length === 0 ? (
        <div className="card p-10 text-center">
          <div className="label-caps mb-2">Nenhum modelo cadastrado</div>
          <div className="text-sm mb-4" style={{ color: "var(--text-dim)" }}>
            Cadastre um modelo de equipamento e depois adicione seus números de série.
          </div>
          {canManage && <button className="btn btn-primary" onClick={openNewModel}><Plus size={16} /> Cadastrar Modelo</button>}
        </div>
      ) : (
        <div className="space-y-3">
          {models.map((m) => (
            <div key={m.id} className="card" data-testid={`equipment-model-${m.id}`}>
              <div className="px-5 py-4 flex items-center gap-4 cursor-pointer" onClick={() => toggle(m.id)}>
                {expanded[m.id] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-semibold" style={{ fontFamily: "Chivo" }}>{m.name}</h3>
                    {m.brand && <span className="badge badge-role-tecnico">{m.brand}</span>}
                    {m.category && <span className="badge badge-role-gestor">{m.category}</span>}
                  </div>
                  {m.description && <div className="text-xs mt-1" style={{ color: "var(--text-mute)" }}>{m.description}</div>}
                </div>
                <div className="hidden md:flex items-center gap-6 text-xs">
                  <Stat label="Total" value={m.total_units} />
                  <Stat label="Estoque" value={m.in_stock} color="#38BDF8" />
                  <Stat label="Com Téc." value={m.with_technician} color="#FACC15" />
                  <Stat label="Instalado" value={m.installed} color="#22C55E" />
                </div>
                {canManage && (
                  <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                    <button className="btn btn-secondary" onClick={() => openAddSns(m.id)} data-testid={`add-sns-btn-${m.id}`}>
                      <Plus size={14} /> Adicionar SNs
                    </button>
                    <button className="btn btn-ghost" onClick={() => openEditModel(m)} data-testid={`edit-model-btn-${m.id}`}><Pencil size={14} /></button>
                    <button className="btn btn-ghost" onClick={() => deleteModel(m.id)} data-testid={`delete-model-btn-${m.id}`}><Trash2 size={14} /></button>
                  </div>
                )}
              </div>

              {expanded[m.id] && (
                <div className="border-t" style={{ borderColor: "var(--border)" }}>
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Nº Série</th>
                        <th>Status</th>
                        <th>Técnico</th>
                        <th>Cliente / OT</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(unitsByModel[m.id] || []).map((u) => {
                        const s = STATUS_LABEL[u.status] || { label: u.status, cls: "badge-open" };
                        return (
                          <tr key={u.id} data-testid={`unit-row-${u.serial_number}`}>
                            <td className="mono font-semibold">{u.serial_number}</td>
                            <td><span className={`badge ${s.cls}`}>{s.label}</span></td>
                            <td>{u.technician_name || "-"}</td>
                            <td className="text-xs" style={{ color: "var(--text-dim)" }}>{u.client_name || (u.work_order_id ? `OT #${u.work_order_id.slice(0, 6)}` : "-")}</td>
                            <td>
                              <div className="flex gap-1">
                                {canManage && u.status === "estoque" && (
                                  <button className="btn btn-ghost" onClick={() => openAssign(u)} data-testid={`assign-unit-${u.serial_number}`}>
                                    <Send size={13} /> Atribuir
                                  </button>
                                )}
                                {canManage && u.status === "com_tecnico" && (
                                  <button className="btn btn-ghost" onClick={() => openAssign(u)} title="Transferir para outro técnico" data-testid={`transfer-unit-${u.serial_number}`}>
                                    <Send size={13} /> Transferir
                                  </button>
                                )}
                                {(canManage || (user?.role === "tecnico" && u.technician_id === user.id)) && u.status === "com_tecnico" && (
                                  <button className="btn btn-ghost" onClick={() => returnUnit(u)} data-testid={`return-unit-${u.serial_number}`}>
                                    <Undo2 size={13} /> Retornar
                                  </button>
                                )}
                                {canManage && u.status !== "instalado" && (
                                  <button className="btn btn-ghost" onClick={() => deleteUnit(u)}><Trash2 size={13} /></button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {(!unitsByModel[m.id] || unitsByModel[m.id].length === 0) && (
                        <tr><td colSpan={5} className="text-center py-8 text-sm" style={{ color: "var(--text-mute)" }}>Nenhuma unidade cadastrada. Clique em "Adicionar SNs".</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal open={modelOpen} onClose={() => setModelOpen(false)} title={editingModel ? "Editar Modelo" : "Novo Modelo de Equipamento"} testid="model-modal"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setModelOpen(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={saveModel} data-testid="save-model-btn">Salvar</button>
        </>}>
        {err && <div className="mb-3 p-2 text-sm border" style={{ background: "rgba(239,68,68,0.08)", borderColor: "rgba(239,68,68,0.3)", color: "#FCA5A5" }}>{err}</div>}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><label className="label-caps block mb-1">Nome</label>
            <input className="input" value={modelForm.name} onChange={(e) => setModelForm({ ...modelForm, name: e.target.value })} placeholder="Ex.: Router ZTE WiFi 6" data-testid="model-name" />
          </div>
          <div><label className="label-caps block mb-1">Marca</label>
            <input className="input" value={modelForm.brand} onChange={(e) => setModelForm({ ...modelForm, brand: e.target.value })} placeholder="ZTE" data-testid="model-brand" />
          </div>
          <div><label className="label-caps block mb-1">Categoria</label>
            <input className="input" value={modelForm.category} onChange={(e) => setModelForm({ ...modelForm, category: e.target.value })} placeholder="Roteador" data-testid="model-category" />
          </div>
          <div className="col-span-2"><label className="label-caps block mb-1">Descrição</label>
            <textarea className="textarea" rows="2" value={modelForm.description} onChange={(e) => setModelForm({ ...modelForm, description: e.target.value })} />
          </div>
        </div>
      </Modal>

      <Modal open={snOpen} onClose={() => setSnOpen(false)} title="Adicionar Números de Série" testid="add-sns-modal"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setSnOpen(false)}>Fechar</button>
          <button className="btn btn-primary" onClick={submitSns} data-testid="submit-sns-btn">Cadastrar</button>
        </>}>
        {snMsg && <div className="mb-3 p-2 text-sm border" style={{ borderColor: "var(--border-strong)", color: "var(--text-dim)" }}>{snMsg}</div>}
        <label className="label-caps block mb-1">Números de Série (um por linha)</label>
        <textarea
          className="textarea mono"
          rows="8"
          placeholder={"SN00000000\nSN45454545\nSN789900"}
          value={snForm.serial_numbers_text}
          onChange={(e) => setSnForm({ ...snForm, serial_numbers_text: e.target.value })}
          data-testid="sns-textarea"
        />
        <div className="text-xs mt-2" style={{ color: "var(--text-mute)" }}>
          Cole uma lista de números de série (separados por linha, vírgula ou espaço). Duplicados são ignorados automaticamente.
        </div>
      </Modal>

      <Modal open={assignOpen} onClose={() => setAssignOpen(false)} title="Atribuir Equipamento a Técnico" testid="assign-unit-modal"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setAssignOpen(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={doAssign} data-testid="confirm-assign-btn">Atribuir</button>
        </>}>
        {assignForm.unit && (
          <>
            <div className="mb-3 p-3 border" style={{ borderColor: "var(--border-strong)" }}>
              <div className="label-caps mb-1">Unidade</div>
              <div className="mono font-semibold">{assignForm.unit.serial_number}</div>
            </div>
            <label className="label-caps block mb-1">Técnico</label>
            <select className="select" value={assignForm.technician_id} onChange={(e) => setAssignForm({ ...assignForm, technician_id: e.target.value })} data-testid="assign-technician-select">
              <option value="">Selecione</option>
              {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </>
        )}
      </Modal>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="text-right">
      <div className="stat-label">{label}</div>
      <div className="mono font-semibold text-base" style={{ color: color || "var(--text)" }}>{value ?? 0}</div>
    </div>
  );
}
